import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const AsparagusPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Asparagus Production: Mastering pH & Water Quality"
        description="Expert guide to water quality management for commercial asparagus production. Learn how pH control increases spear quality, crown longevity, and consistent yields."
        canonicalPath="/asparagus"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5418994/pexels-photo-5418994.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Fresh green asparagus spears"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 via-emerald-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🌱</span>
              <span>Premium Commercial Resource for Growers</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Asparagus Production:
              <span className="block text-emerald-300">Mastering pH & Water Quality</span>
            </h1>

            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Science-backed irrigation strategies to maximize spear quality and crown longevity
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-emerald-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-emerald-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🌱</span>
              <span>Introduction</span>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-green-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Asparagus is a long-term investment crop</strong> — with crowns producing for 15-20 years or more, water quality decisions you make today affect profitability for decades to come.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've worked with growers around the world for over 40 years. This guide explains why irrigation water pH and bicarbonate management are critical for spear quality, crown health, and consistent yields in alkaline soil environments.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">

          {/* Section 1: Asparagus pH Requirements */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Asparagus pH Requirements & Tolerance
              </h2>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Asparagus performs best in soils with pH between 6.5 and 7.5, though it can tolerate a wider range than many crops (6.0-8.0). However, optimal nutrient availability, particularly phosphorus and micronutrients like iron and zinc, occurs in the 6.5-7.5 range. When soil pH drifts above 7.5 due to alkaline irrigation water, nutrient lockup becomes a serious concern."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <a
                  href="https://extension.psu.edu/asparagus-production"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-green-600 hover:text-green-800 hover:underline transition-colors"
                >
                  Source: Penn State Extension — Asparagus Production Guide
                </a>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">Key Takeaway:</span>
                </div>
                <p className="text-gray-700">While asparagus is relatively pH-tolerant, irrigation water quality significantly impacts long-term crown health and spear uniformity.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Bicarbonate Water Impact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: High-Bicarbonate Water & Crown Health
              </h2>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Asparagus crowns store carbohydrates in fleshy storage roots that can extend 5-6 feet deep. These extensive root systems are exposed to cumulative effects of irrigation water chemistry season after season. High-bicarbonate water gradually raises soil pH throughout the root zone, potentially affecting nutrient uptake and crown vigor over the life of the planting.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "In asparagus production systems in the western U.S., irrigation water with high bicarbonate levels (above 120 ppm) can contribute to gradual soil pH increases, especially in the upper 2-3 feet where feeder roots are concentrated. This pH drift can reduce phosphorus availability and cause micronutrient deficiencies that manifest as reduced spear size and quality."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <a
                  href="https://anrcatalog.ucanr.edu/Details.aspx?itemNo=8326"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-orange-600 hover:text-orange-800 hover:underline transition-colors"
                >
                  Source: UC ANR — Asparagus Production in California
                </a>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Test your irrigation water's bicarbonate levels annually. Long-term exposure to high-bicarbonate water can gradually reduce crown productivity and spear quality.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Phosphorus Availability */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔬</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Phosphorus & Micronutrient Management
              </h2>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Asparagus has high phosphorus requirements, particularly in the early spring when spears are emerging rapidly. At soil pH above 7.5, phosphorus becomes increasingly unavailable due to precipitation with calcium carbonate. Similarly, iron and zinc deficiencies become more common in high-pH conditions, potentially reducing spear quality and uniformity."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <a
                  href="https://www.michigan.gov/mda/environment/fertilizers/nutrient-management/asparagus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  Source: Michigan State University Extension — Asparagus Nutrient Management
                </a>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">Grower Tip:</span>
                </div>
                <p className="text-gray-700">Spears that emerge thin, with tight tips or poor color, may indicate phosphorus or micronutrient deficiency related to high soil pH.</p>
              </div>

              {/* Nutrient Availability Chart */}
              <div className="mt-6 bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Nutrient Availability vs. pH</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">pH 6.5-7.5 (Optimal)</span>
                    <div className="w-48 bg-gray-200 rounded-full h-4">
                      <div className="bg-emerald-500 h-4 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                    <span className="text-emerald-600 font-semibold">95%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">pH 7.5-8.0</span>
                    <div className="w-48 bg-gray-200 rounded-full h-4">
                      <div className="bg-yellow-500 h-4 rounded-full" style={{ width: '70%' }}></div>
                    </div>
                    <span className="text-yellow-600 font-semibold">70%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">pH 8.0+</span>
                    <div className="w-48 bg-gray-200 rounded-full h-4">
                      <div className="bg-red-500 h-4 rounded-full" style={{ width: '40%' }}></div>
                    </div>
                    <span className="text-red-600 font-semibold">40%</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Drip Irrigation Challenges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: Drip Irrigation & Emitter Management
              </h2>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "Many commercial asparagus operations use drip irrigation to deliver water and nutrients directly to the root zone. In high-pH water, calcium and magnesium carbonate precipitation can clog emitters over time, creating uneven water distribution that directly affects spear uniformity and marketable yield. Maintaining consistent water delivery is critical in asparagus, where harvest quality depends on uniform moisture."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <a
                  href="https://irrigationtoolbox.com/ReferenceDocuments/Extension/California/583.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-purple-600 hover:text-purple-800 hover:underline transition-colors"
                >
                  Source: University of California — Asparagus Irrigation Management
                </a>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">Why It Matters:</span>
                </div>
                <p className="text-gray-700">pH management with sulfur burners prevents carbonate buildup in drip lines, maintaining uniform water distribution critical for premium spear production.</p>
              </div>

              {/* System Comparison */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-pH Water Issues:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Emitter clogging and uneven distribution</li>
                    <li>• Inconsistent spear emergence</li>
                    <li>• Variable spear diameter and quality</li>
                    <li>• Reduced marketable yield</li>
                    <li>• Increased maintenance costs</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Control:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform spear emergence</li>
                    <li>• Consistent premium quality</li>
                    <li>• Higher marketable yield</li>
                    <li>• Lower system maintenance</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Salinity Management */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">⚖️</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Salinity & Sodium Management in Asparagus
              </h2>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-teal-500 pl-6">
                "Asparagus is moderately tolerant to salinity (threshold EC of 4.1 dS/m), but sodium accumulation from poor-quality irrigation water can cause soil structural degradation and reduced infiltration over time. This is particularly problematic in heavy soils. pH management helps improve calcium availability, which can displace sodium from soil exchange sites and improve drainage."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-teal-600" size={16} />
                <a
                  href="https://www.fao.org/3/t0667e/t0667e0b.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-teal-600 hover:text-teal-800 hover:underline transition-colors"
                >
                  Source: FAO — Crop Salt Tolerance Data
                </a>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">Long-Term Protection:</span>
                </div>
                <p className="text-gray-700">Managing irrigation water pH helps maintain soil structure and infiltration rates critical for long-lived asparagus plantings.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 6: Sulfur Burners Solution */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔄</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                6: Sulfur Burners — The Professional Solution
              </h2>
            </div>

            <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 md:p-8 border border-ctc-blue/20">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-ctc-blue pl-6">
                "Inline sulfur burners provide a safe, automated method to reduce irrigation water pH and neutralize bicarbonates before water reaches the asparagus root zone. For growers managing multi-year plantings, sulfur burners eliminate the need to handle bulk sulfuric acid while providing consistent, reliable pH control season after season."
              </blockquote>

              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-ctc-blue" size={16} />
                <span className="text-sm font-medium text-ctc-blue">
                  Source: CTC Waterworks & Western grower trials
                </span>
              </div>

              <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-emerald-600" size={20} />
                  <span className="font-semibold text-emerald-800">Added Benefit:</span>
                </div>
                <p className="text-gray-700">Sulfur burners also provide sulfate-sulfur nutrition — important for asparagus quality and flavor development.</p>
              </div>

              {/* CTC Advantages */}
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why CTC Sulfur Burners Are Ideal for Asparagus Production:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents drip emitter clogging and system failures',
                    'No hazardous acid handling or storage requirements',
                    'Automated operation — runs with your irrigation schedule',
                    'Provides sulfur nutrition as a production bonus',
                    'Built for 15-20+ year asparagus production cycles',
                    'Proven in commercial operations throughout the West',
                    'Expert technical support from experienced agronomists'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Asparagus" />

      <Footer />
    </div>
  );
};

export default AsparagusPage;
